import { useEffect, useState } from "react";
import { api, useSeo, setAdminToken, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Database, LogOut, Lock, Pencil, X, Wand2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Auto-derive store info from a website URL
function parseUrl(input) {
  if (!input) return null;
  const raw = input.trim();
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(withScheme);
    const host = u.hostname.replace(/^www\./, "");
    const rootName = host.split(".")[0];
    return {
      url: `${u.protocol}//${host}`,
      host,
      slug: rootName.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
      name: rootName.charAt(0).toUpperCase() + rootName.slice(1),
      logo: `https://logo.clearbit.com/${host}`,
    };
  } catch { return null; }
}

function LoginGate({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdminToken(data.access_token);
      toast.success("Welcome back, admin");
      onLogin();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login-gate">
      <div className="w-full max-w-md border border-slate-200 bg-white p-10 rounded-sm">
        <div className="w-12 h-12 bg-slate-950 flex items-center justify-center rounded-sm mb-6">
          <Lock className="w-5 h-5 text-emerald-400" />
        </div>
        <p className="overline mb-3">Restricted</p>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter uppercase mb-2">Admin sign in</h1>
        <p className="text-sm text-slate-600 mb-8">Editor access only. Sessions expire after 12 hours.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="overline text-slate-500 block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:border-slate-950 focus:outline-none"
              data-testid="admin-email-input"
              placeholder="admin@dealvault.com"
            />
          </div>
          <div>
            <label className="overline text-slate-500 block mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:border-slate-950 focus:outline-none"
              data-testid="admin-password-input"
            />
          </div>
          {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full py-3 bg-slate-950 hover:bg-emerald-500 hover:text-slate-950 text-white font-bold text-sm tracking-wide rounded-sm transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminPanel({ onLogout }) {
  const [stats, setStats] = useState({});
  const [stores, setStores] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coupon, setCoupon] = useState({ title: "", code: "", discount_label: "", store_slug: "", deal_type: "code" });
  const [store, setStore] = useState({ name: "", slug: "", logo_url: "", website: "", description: "", category_slug: "", featured: false });
  const [editing, setEditing] = useState(null); // coupon being edited
  const [editingStore, setEditingStore] = useState(null);

  // Auto-fill store fields from pasted website URL
  const handleWebsiteChange = (value) => {
    const parsed = parseUrl(value);
    setStore((prev) => ({
      ...prev,
      website: value,
      // Only overwrite blank fields — respect anything the admin already typed
      slug: prev.slug || parsed?.slug || "",
      name: prev.name || parsed?.name || "",
      logo_url: prev.logo_url || parsed?.logo || "",
    }));
  };

  const load = () => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/stores", { params: { limit: 500 } }).then((r) => setStores(r.data));
    api.get("/coupons", { params: { limit: 500, sort: "recent", include_expired: true } }).then((r) => setCoupons(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  };
  useEffect(() => { load(); }, []);

  const handleAuthError = (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      setAdminToken(null);
      toast.error("Session expired");
      onLogout();
      return true;
    }
    return false;
  };

  const addStore = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stores", store);
      toast.success("Store added");
      setStore({ name: "", slug: "", logo_url: "", website: "", description: "", category_slug: "", featured: false });
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const addCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", { ...coupon, category_slug: stores.find(s => s.slug === coupon.store_slug)?.category_slug || null });
      toast.success("Coupon added");
      setCoupon({ title: "", code: "", discount_label: "", store_slug: "", deal_type: "code" });
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const seed = async () => {
    if (!window.confirm("Force reseed will wipe all data. Continue?")) return;
    try {
      await api.post("/seed", null, { params: { force: true } });
      toast.success("Reseeded");
      load();
    } catch (err) { handleAuthError(err) || toast.error("Failed"); }
  };

  const delStore = async (slug) => {
    if (!window.confirm(`Delete store "${slug}"?`)) return;
    try {
      await api.delete(`/stores/${slug}`);
      toast.success("Deleted");
      load();
    } catch (err) { handleAuthError(err) || toast.error("Failed"); }
  };

  const delCoupon = async (id) => {
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Coupon removed");
      load();
    } catch (err) { handleAuthError(err) || toast.error("Failed"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const patch = {
      title: editing.title,
      code: editing.code,
      discount_label: editing.discount_label,
      deal_type: editing.deal_type,
      verified: !!editing.verified,
      featured: !!editing.featured,
    };
    try {
      await api.patch(`/coupons/${editing.id}`, patch);
      toast.success("Coupon updated");
      setEditing(null);
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const saveStoreEdit = async (e) => {
    e.preventDefault();
    if (!editingStore) return;
    const patch = {
      name: editingStore.name,
      description: editingStore.description,
      logo_url: editingStore.logo_url,
      website: editingStore.website,
      category_slug: editingStore.category_slug || null,
      featured: !!editingStore.featured,
      seo_title: editingStore.seo_title || null,
      seo_description: editingStore.seo_description || null,
      seo_keywords: editingStore.seo_keywords || null,
    };
    try {
      await api.patch(`/stores/${editingStore.slug}`, patch);
      toast.success("Store updated");
      setEditingStore(null);
      load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="admin-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline mb-3">Control room</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter uppercase">Admin Panel</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={seed} data-testid="admin-reseed-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-slate-950 hover:bg-slate-950 hover:text-white rounded-sm text-sm font-medium transition-colors">
            <Database className="w-4 h-4" /> Force Reseed
          </button>
          <button onClick={onLogout} data-testid="admin-logout-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 hover:border-red-500 hover:text-red-600 rounded-sm text-sm font-medium transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Stores", value: stats.stores },
          { label: "Coupons", value: stats.coupons },
          { label: "Categories", value: stats.categories },
          { label: "Subscribers", value: stats.newsletter_subs },
        ].map((s) => (
          <div key={s.label} className="border border-slate-200 p-6 rounded-sm bg-white" data-testid={`stat-${s.label.toLowerCase()}`}>
            <p className="overline text-slate-500 mb-2">{s.label}</p>
            <p className="font-display text-4xl tracking-tighter">{s.value ?? "—"}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="coupons">
        <TabsList className="mb-6" data-testid="admin-tabs">
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="stores" data-testid="tab-stores">Stores</TabsTrigger>
        </TabsList>

        <TabsContent value="coupons">
          <form onSubmit={addCoupon} className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-8 p-5 border border-slate-200 rounded-sm bg-slate-50" data-testid="add-coupon-form">
            <input required placeholder="Coupon title" value={coupon.title} onChange={(e) => setCoupon({ ...coupon, title: e.target.value })}
                   className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="coupon-title-input" />
            <input placeholder="CODE" value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })}
                   className="px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" data-testid="coupon-code-input" />
            <input required placeholder="20% OFF" value={coupon.discount_label} onChange={(e) => setCoupon({ ...coupon, discount_label: e.target.value })}
                   className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="coupon-label-input" />
            <select required value={coupon.store_slug} onChange={(e) => setCoupon({ ...coupon, store_slug: e.target.value })}
                    className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="coupon-store-select">
              <option value="">Select store…</option>
              {stores.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
            <button type="submit" className="px-4 py-2 bg-emerald-500 text-slate-950 font-bold text-sm rounded-sm hover:bg-emerald-400 flex items-center justify-center gap-2" data-testid="add-coupon-submit">
              <Plus className="w-4 h-4" /> Add
            </button>
          </form>

          <div className="border border-slate-200 rounded-sm overflow-hidden overflow-x-auto" data-testid="coupons-list">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="p-3 overline text-slate-500">Discount</th>
                  <th className="p-3 overline text-slate-500">Store</th>
                  <th className="p-3 overline text-slate-500">Title</th>
                  <th className="p-3 overline text-slate-500">Code</th>
                  <th className="p-3 overline text-slate-500">Uses</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {coupons.slice(0, 40).map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-display font-bold">{c.discount_label}</td>
                    <td className="p-3">{c.store_name}</td>
                    <td className="p-3">{c.title}</td>
                    <td className="p-3 font-mono text-xs">{c.code || "—"}</td>
                    <td className="p-3">{c.uses}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditing({ ...c })} data-testid={`edit-coupon-${c.id}`}
                              className="p-1.5 hover:bg-emerald-50 hover:text-emerald-700 rounded-sm transition-colors mr-1"
                              title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => delCoupon(c.id)} data-testid={`del-coupon-${c.id}`}
                              className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="stores">
          <form onSubmit={addStore} className="p-5 border border-slate-200 rounded-sm bg-slate-50 mb-8" data-testid="add-store-form">
            <div className="mb-4 flex items-center gap-2 text-xs text-slate-500">
              <Wand2 className="w-4 h-4 text-emerald-500" />
              <span>Paste the store URL and we&apos;ll auto-fill the name, slug, and logo.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
              <input
                required
                placeholder="https://nike.com"
                value={store.website}
                onChange={(e) => handleWebsiteChange(e.target.value)}
                onBlur={(e) => handleWebsiteChange(e.target.value)}
                className="md:col-span-3 px-3 py-2 border-2 border-emerald-300 focus:border-emerald-500 rounded-sm text-sm bg-white focus:outline-none"
                data-testid="store-website-input"
              />
              <input
                required
                placeholder="Nike"
                value={store.name}
                onChange={(e) => setStore({ ...store, name: e.target.value })}
                className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="store-name-input"
              />
              <select
                value={store.category_slug}
                onChange={(e) => setStore({ ...store, category_slug: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
              >
                <option value="">Category…</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input
                required
                placeholder="nike"
                value={store.slug}
                onChange={(e) => setStore({ ...store, slug: e.target.value.toLowerCase() })}
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                data-testid="store-slug-input"
              />
              <div className="md:col-span-4 relative">
                <input
                  placeholder="Logo URL (auto-filled)"
                  value={store.logo_url}
                  onChange={(e) => setStore({ ...store, logo_url: e.target.value })}
                  className="w-full px-3 py-2 pr-14 border border-slate-300 rounded-sm text-sm"
                  data-testid="store-logo-input"
                />
                {store.logo_url && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 border border-slate-200 bg-white rounded-sm flex items-center justify-center overflow-hidden" data-testid="logo-preview">
                    <img
                      src={store.logo_url}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-500 text-slate-950 font-bold text-sm rounded-sm hover:bg-emerald-400 flex items-center justify-center gap-2"
                data-testid="add-store-submit"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </form>

          <div className="border border-slate-200 rounded-sm overflow-hidden overflow-x-auto" data-testid="stores-list">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="p-3 overline text-slate-500">Name</th>
                  <th className="p-3 overline text-slate-500">Slug</th>
                  <th className="p-3 overline text-slate-500">Category</th>
                  <th className="p-3 overline text-slate-500">Coupons</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {stores.map((s) => (
                  <tr key={s.slug} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium">{s.name}</td>
                    <td className="p-3 font-mono text-xs">{s.slug}</td>
                    <td className="p-3">{s.category_slug || "—"}</td>
                    <td className="p-3">{s.coupon_count}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditingStore({ ...s })} data-testid={`edit-store-${s.slug}`}
                              className="p-1.5 hover:bg-emerald-50 hover:text-emerald-700 rounded-sm mr-1"
                              title="Edit store & SEO">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => delStore(s.slug)} data-testid={`del-store-${s.slug}`}
                              className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setEditing(null)} data-testid="edit-coupon-modal">
          <div className="bg-white rounded-sm border border-slate-200 max-w-lg w-full p-8"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="overline mb-2">Edit coupon</p>
                <h3 className="font-display text-2xl tracking-tight">{editing.store_name}</h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-sm"
                      data-testid="edit-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4" data-testid="edit-coupon-form">
              <div>
                <label className="overline text-slate-500 block mb-2">Title</label>
                <input required value={editing.title}
                       onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                       className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                       data-testid="edit-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-slate-500 block mb-2">Code</label>
                  <input value={editing.code || ""}
                         onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                         className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                         data-testid="edit-code-input" />
                </div>
                <div>
                  <label className="overline text-slate-500 block mb-2">Discount label</label>
                  <input required value={editing.discount_label}
                         onChange={(e) => setEditing({ ...editing, discount_label: e.target.value })}
                         className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                         data-testid="edit-label-input" />
                </div>
              </div>
              <div>
                <label className="overline text-slate-500 block mb-2">Type</label>
                <select value={editing.deal_type}
                        onChange={(e) => setEditing({ ...editing, deal_type: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                        data-testid="edit-type-select">
                  <option value="code">Code</option>
                  <option value="deal">Deal (no code)</option>
                </select>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.verified}
                         onChange={(e) => setEditing({ ...editing, verified: e.target.checked })}
                         data-testid="edit-verified-checkbox" />
                  Verified
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.featured}
                         onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                         data-testid="edit-featured-checkbox" />
                  Featured
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditing(null)}
                        data-testid="edit-cancel-btn"
                        className="flex-1 py-3 border border-slate-300 rounded-sm text-sm font-medium hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" data-testid="edit-save-btn"
                        className="flex-1 py-3 bg-emerald-500 text-slate-950 font-bold text-sm rounded-sm hover:bg-emerald-400">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingStore && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setEditingStore(null)} data-testid="edit-store-modal">
          <div className="bg-white rounded-sm border border-slate-200 max-w-2xl w-full p-8 max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="overline mb-2">Edit store & SEO</p>
                <h3 className="font-display text-2xl tracking-tight">{editingStore.name}</h3>
              </div>
              <button onClick={() => setEditingStore(null)} className="p-1.5 hover:bg-slate-100 rounded-sm"
                      data-testid="edit-store-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={saveStoreEdit} className="space-y-5" data-testid="edit-store-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-slate-500 block mb-2">Name</label>
                  <input required value={editingStore.name}
                         onChange={(e) => setEditingStore({ ...editingStore, name: e.target.value })}
                         className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                         data-testid="edit-store-name-input" />
                </div>
                <div>
                  <label className="overline text-slate-500 block mb-2">Website URL</label>
                  <input value={editingStore.website || ""}
                         onChange={(e) => setEditingStore({ ...editingStore, website: e.target.value })}
                         className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
              </div>
              <div>
                <label className="overline text-slate-500 block mb-2">Description</label>
                <textarea rows={3} value={editingStore.description || ""}
                          onChange={(e) => setEditingStore({ ...editingStore, description: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline text-slate-500 block mb-2">Logo URL</label>
                  <div className="flex gap-2">
                    <input value={editingStore.logo_url || ""}
                           onChange={(e) => setEditingStore({ ...editingStore, logo_url: e.target.value })}
                           className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                    <button
                      type="button"
                      title="Auto-fetch logo from Website URL"
                      onClick={() => {
                        const p = parseUrl(editingStore.website);
                        if (!p) return toast.error("Enter a valid website URL first");
                        setEditingStore({ ...editingStore, logo_url: p.logo });
                        toast.success("Logo URL fetched");
                      }}
                      data-testid="regen-logo-btn"
                      className="px-3 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm text-xs font-medium flex items-center gap-1"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Fetch
                    </button>
                  </div>
                  {editingStore.logo_url && (
                    <div className="mt-2 w-12 h-12 border border-slate-200 bg-white rounded-sm flex items-center justify-center p-1">
                      <img src={editingStore.logo_url} alt="preview" className="max-w-full max-h-full object-contain"
                           onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                  )}
                </div>
                <div>
                  <label className="overline text-slate-500 block mb-2">Category</label>
                  <select value={editingStore.category_slug || ""}
                          onChange={(e) => setEditingStore({ ...editingStore, category_slug: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
                    <option value="">— None —</option>
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editingStore.featured}
                       onChange={(e) => setEditingStore({ ...editingStore, featured: e.target.checked })} />
                Featured on homepage
              </label>

              <div className="pt-4 border-t border-slate-200">
                <p className="overline mb-3 text-emerald-600">SEO overrides (leave blank for auto)</p>
                <div className="space-y-4">
                  <div>
                    <label className="overline text-slate-500 block mb-2">SEO title</label>
                    <input maxLength={70} value={editingStore.seo_title || ""}
                           onChange={(e) => setEditingStore({ ...editingStore, seo_title: e.target.value })}
                           placeholder={`${editingStore.name} Coupons & Promo Codes 2026 — SharePromos`}
                           className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                           data-testid="edit-store-seo-title" />
                    <p className="text-xs text-slate-400 mt-1">{(editingStore.seo_title || "").length}/70</p>
                  </div>
                  <div>
                    <label className="overline text-slate-500 block mb-2">SEO description</label>
                    <textarea rows={2} maxLength={160} value={editingStore.seo_description || ""}
                              onChange={(e) => setEditingStore({ ...editingStore, seo_description: e.target.value })}
                              placeholder="Auto: {N} verified {store} coupon codes, promo codes & cashback deals..."
                              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none"
                              data-testid="edit-store-seo-description" />
                    <p className="text-xs text-slate-400 mt-1">{(editingStore.seo_description || "").length}/160</p>
                  </div>
                  <div>
                    <label className="overline text-slate-500 block mb-2">SEO keywords</label>
                    <textarea rows={2} value={editingStore.seo_keywords || ""}
                              onChange={(e) => setEditingStore({ ...editingStore, seo_keywords: e.target.value })}
                              placeholder="{store} coupons, {store} promo code, {store} discount, ..."
                              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none"
                              data-testid="edit-store-seo-keywords" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditingStore(null)}
                        className="flex-1 py-3 border border-slate-300 rounded-sm text-sm font-medium hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" data-testid="edit-store-save-btn"
                        className="flex-1 py-3 bg-emerald-500 text-slate-950 font-bold text-sm rounded-sm hover:bg-emerald-400">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  useSeo("Admin — SharePromos", "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then(() => { setAuthed(true); setChecking(false); })
      .catch(() => { setAdminToken(null); setChecking(false); });
  }, []);

  if (checking) return <div className="max-w-3xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;
  if (!authed) return <LoginGate onLogin={() => setAuthed(true)} />;
  return <AdminPanel onLogout={() => { setAdminToken(null); setAuthed(false); }} />;
}
